import { useState, type FormEvent } from 'react'
import { useApiClients } from '@/api/api-clients-context'
import { LinkNoLongerUsableError } from '@/api/auth-link-grants-client'
import type { LinkGrantVerifyResult } from '@/api/types'
import { isWellFormedIsoDate } from '@/lib/demographics'
import { useI18n } from '@/i18n/i18n-context'
import { Button } from '@/ui/button'
import { Card } from '@/ui/card'
import { Input } from '@/ui/input'
import { Label } from '@/ui/label'
import { MAX_DATE_OF_BIRTH_ATTEMPTS } from '../flow-state'

interface Props {
  readonly token: string
  readonly onVerified: (verified: LinkGrantVerifyResult) => void
  readonly onDead: () => void
}

/**
 * verify()'s failure response is bit-for-bit identical whether this grant has attempts left or is
 * already dead (D-8) — the server enforces the real 3-attempt cap, but tells this screen nothing
 * about how many remain. Offering unlimited retries would silently exceed a cap this screen cannot
 * see is already spent; refusing any retry at all would punish an honest typo on the first attempt.
 * The resolution is a *local* budget matching the architecture's own published cap
 * (platform-auth.md §5) rather than one read off any response: up to MAX_DATE_OF_BIRTH_ATTEMPTS
 * submissions get the same inline "that didn't match" wording — with no attempt count in it, since
 * this screen's local counter could be wrong about the server's real one (a flood on another device
 * could already have spent it) and stating a number would be inventing a fact — and only once the
 * local budget is spent does the flow move to DeadLinkScreen.
 */
export function DateOfBirthScreen({ token, onVerified, onDead }: Props) {
  const { auth } = useApiClients()
  const { t } = useI18n()
  const [value, setValue] = useState('')
  const [attempts, setAttempts] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [showMismatch, setShowMismatch] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!isWellFormedIsoDate(value) || submitting) {
      return
    }
    setSubmitting(true)
    setShowMismatch(false)
    try {
      const result = await auth.verify(token, value)
      onVerified(result)
    } catch (error) {
      if (!(error instanceof LinkNoLongerUsableError)) {
        throw error
      }
      const nextAttempts = attempts + 1
      setAttempts(nextAttempts)
      if (nextAttempts >= MAX_DATE_OF_BIRTH_ATTEMPTS) {
        onDead()
      } else {
        setShowMismatch(true)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card className="flex flex-col gap-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">{t('dateOfBirthPrompt')}</h1>
      </div>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="date-of-birth">{t('dateOfBirthLabel')}</Label>
          <Input
            id="date-of-birth"
            type="date"
            required
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        </div>
        {showMismatch && (
          <p role="alert" className="text-sm text-red-600">
            {t('dateOfBirthMismatch')}
          </p>
        )}
        <Button type="submit" disabled={!isWellFormedIsoDate(value) || submitting}>
          {t('continue')}
        </Button>
      </form>
    </Card>
  )
}
